import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, MapPin, Clock, ArrowRight, Filter } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const eventTypes = [
  { id: 'all', label: 'All Events' },
  { id: 'registration', label: 'Registration' },
  { id: 'fundraiser', label: 'Fundraisers' },
  { id: 'volunteer', label: 'Volunteer Days' },
  { id: 'competition', label: 'Competitions' },
];

const Events = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedType, setSelectedType] = useState('all');

  useEffect(() => {
    const sampleEvents = [
      {
        id: '1',
        title: 'Spring Track & Field Registration',
        description: 'Register now for our Spring 2026 youth track and field program. Open to kids ages 6-18.',
        date: 'March 15, 2026',
        time: '9:00 AM - 12:00 PM',
        location: 'Lincoln High School Track',
        event_type: 'registration',
      },
      {
        id: '2',
        title: 'Annual Fundraising Gala',
        description: 'Join us for an evening of celebration and support. Dinner, silent auction, and athlete recognition.',
        date: 'April 20, 2026',
        time: '6:00 PM - 10:00 PM',
        location: 'Downtown Convention Center',
        event_type: 'fundraiser',
      },
      {
        id: '3',
        title: 'Community Volunteer Day',
        description: 'Help us prepare equipment and facilities for the upcoming season. All skill levels welcome!',
        date: 'March 1, 2026',
        time: '10:00 AM - 2:00 PM',
        location: 'Lane 4 Community Center',
        event_type: 'volunteer',
      },
    ];

    const fetchEvents = async () => {
      try {
        const response = await axios.get(`${API}/events`);
        // Show real events if any exist; otherwise fall back to samples
        setEvents(response.data && response.data.length > 0 ? response.data : sampleEvents);
      } catch (error) {
        console.error('Error fetching events:', error);
        setEvents(sampleEvents);
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, []);

  const filteredEvents = selectedType === 'all'
    ? events
    : events.filter(e => e.event_type === selectedType);

  const getEventTypeColor = (type) => {
    const colors = {
      registration: 'bg-blue-100 text-blue-700',
      fundraiser: 'bg-amber-100 text-amber-700',
      volunteer: 'bg-green-100 text-green-700',
      competition: 'bg-purple-100 text-purple-700',
    };
    return colors[type] || 'bg-slate-100 text-slate-700';
  };

  return (
    <div className="pt-20" data-testid="events-page">
      {/* Hero Section */}
      <section className="py-20 md:py-32 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Calendar className="w-16 h-16 text-red-500 mx-auto mb-6" />
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold">
              Upcoming Events
            </h1>
            <p className="mt-6 text-xl text-slate-300 max-w-2xl mx-auto">
              Join us at community events, fundraisers, and volunteer opportunities. Every event brings us closer to our mission.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Events Section */}
      <section className="py-20 md:py-32 bg-white" data-testid="events-list-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Filters */}
          <div className="flex flex-wrap items-center gap-3 mb-12">
            <Filter className="w-5 h-5 text-slate-400" />
            {eventTypes.map((type) => (
              <button
                key={type.id}
                onClick={() => setSelectedType(type.id)}
                className={`px-4 py-2 text-sm font-medium transition-colors ${
                  selectedType === type.id
                    ? 'bg-slate-900 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
                data-testid={`filter-${type.id}`}
              >
                {type.label}
              </button>
            ))}
          </div>

          {/* Events Grid */}
          {loading ? (
            <div className="text-center py-12">
              <div className="spinner mx-auto" />
              <p className="mt-4 text-slate-600">Loading events...</p>
            </div>
          ) : filteredEvents.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-lg text-slate-600">No events found for this category.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredEvents.map((event, index) => (
                <motion.div
                  key={event.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white border border-slate-200 hover:shadow-xl transition-shadow overflow-hidden"
                  data-testid={`event-card-${index}`}
                >
                  <div className="relative h-48 bg-slate-100">
                    <div className="absolute inset-0 bg-gradient-to-br from-slate-900 to-slate-700 flex items-center justify-center">
                      <Calendar className="w-16 h-16 text-white/30" />
                    </div>
                    <div className="event-date-badge">
                      {event.event_type}
                    </div>
                  </div>
                  <div className="p-6">
                    <span className={`inline-block px-3 py-1 text-xs font-medium uppercase tracking-wider ${getEventTypeColor(event.event_type)}`}>
                      {event.event_type}
                    </span>
                    <h3 className="font-heading text-xl font-bold text-slate-900 mt-3">{event.title}</h3>
                    <p className="mt-2 text-slate-600 text-sm line-clamp-2">{event.description}</p>
                    
                    <div className="mt-4 space-y-2">
                      <div className="flex items-center text-sm text-slate-500">
                        <Calendar className="w-4 h-4 mr-2 text-red-600" />
                        {event.date}
                      </div>
                      <div className="flex items-center text-sm text-slate-500">
                        <Clock className="w-4 h-4 mr-2 text-red-600" />
                        {event.time}
                      </div>
                      <div className="flex items-center text-sm text-slate-500">
                        <MapPin className="w-4 h-4 mr-2 text-red-600" />
                        {event.location}
                      </div>
                    </div>

                    <Button className="w-full mt-6 bg-slate-900 text-white hover:bg-red-600 rounded-none uppercase tracking-wider text-sm font-bold">
                      Learn More
                    </Button>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Host an Event CTA */}
      <section className="py-20 md:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-red-600 font-bold uppercase tracking-widest text-sm">Get Involved</span>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mt-4">
                Host a Fundraiser
              </h2>
              <p className="mt-4 text-lg text-slate-600">
                Organize a community fundraiser, athletic event, or awareness campaign in support of Lane 4. We'll provide materials and support.
              </p>
              <Link to="/contact" className="inline-block mt-8" data-testid="host-event-btn">
                <Button className="bg-red-600 text-white hover:bg-slate-900 rounded-none uppercase tracking-widest font-bold px-8 py-4">
                  Contact Us
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>
            <div className="bg-white p-8 border border-slate-200">
              <h3 className="font-heading text-xl font-bold text-slate-900 mb-4">Event Ideas</h3>
              <ul className="space-y-3">
                {['5K Run/Walk Fundraiser', 'Youth Sports Tournament', 'Corporate Team Challenge', 'Charity Auction Night', 'Community Sports Day'].map((idea, i) => (
                  <li key={i} className="flex items-center text-slate-700">
                    <div className="w-2 h-2 bg-red-600 mr-3" />
                    {idea}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-24 bg-red-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white">
            Can't Attend in Person?
          </h2>
          <p className="mt-4 text-xl text-red-100 max-w-2xl mx-auto">
            Support our events and programs with a donation from anywhere.
          </p>
          <div className="mt-8">
            <Link to="/donate" data-testid="events-donate-btn">
              <Button className="bg-white text-red-600 hover:bg-slate-900 hover:text-white rounded-none uppercase tracking-widest font-bold px-10 py-6 text-base">
                Donate Now
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Events;
